import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getUserSubscriptionAPI,
  subscribePlanAPI,
  createCheckoutSessionAPI,
  confirmPaymentIntentAPI,
} from "../../redux/features/paymentSlice";
import masterCard from "../../assets/images/master-card.png";
import paypalCard from "../../assets/images/paypal-card.png";
import unionPay from "../../assets/images/union-pay.png";
import visaCard from "../../assets/images/visa.png";
import { getPlansAPI } from "../../redux/features/authSlice";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

// ========== CheckoutForm ==========
const CheckoutForm = ({ plan, clientSecret, transactionId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const auth = useSelector((state) => state.auth);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      if (!stripe || !elements) throw new Error("Stripe or Elements not initialized.");
      if (!clientSecret || !transactionId) throw new Error("Missing clientSecret or transactionId.");

      const cardNumberElement = elements.getElement(CardNumberElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardNumberElement },
      });

      if (stripeError) throw new Error(stripeError.message);

      if (paymentIntent.status === "succeeded") {
        await dispatch(
          confirmPaymentIntentAPI({
            token,
            paymentIntentId: paymentIntent.id,
            transactionId,
          })
        ).unwrap();

        toast.success("Payment successful! Subscription activated.", { position: "top-center" });
        localStorage.removeItem(`transaction_${plan.id}`);
        const nextPath = window.location.pathname.includes("student-checkout")
          ? "/manage-subscription"
          : "/add-education";
        setTimeout(() => navigate(nextPath), 2000);
        await dispatch(getPlansAPI(token)).unwrap();

      } else if (paymentIntent.status === "requires_action") {
        toast.info("Completing additional authentication...", { position: "top-center" });
      } else {
        throw new Error(`Payment failed with status: ${paymentIntent.status}`);
      }
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
      toast.error(err.message || "Payment failed. Please try again.", { position: "top-center" });
    } finally {
      setIsProcessing(false);
    }
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#000",
        fontFamily: "inherit",
        "::placeholder": { color: "#AAB7C4" },
      },
      invalid: { color: "#DC3545" },
    },
    classes: { base: "form-control py-16" },
  };

  const cardsImg = [masterCard, paypalCard, unionPay, visaCard];

  return (
    <form onSubmit={handleSubmit} className="flex-grow-1">
      <div className="checkout-wrapper mx-auto px-10 d-flex flex-column flex-md-row justify-content-between gap-4 gap-md-0">
        <div className="checkout-form">
          <div className="mb-14">
            <input
              type="text"
              placeholder="Enter your Card Email Address"
              className="form-control"
              defaultValue={auth?.email || ""}
              readOnly
            />
          </div>
          <div className="d-flex my-14 my-sm-20 my-md-34">
            {cardsImg.map((card, index) => (
              <img key={index} src={card} className="img-fluid me-18" alt="Card logo" />
            ))}
          </div>

          <div className="mb-14">
            <input
              type="text"
              placeholder="Enter your Card Name"
              className="form-control"
              defaultValue={auth?.full_name || ""}
            />
          </div>

          <div className="mb-14">
            <CardNumberElement options={elementOptions} />
          </div>
          <div className="row">
            <div className="col-sm-6">
              <CardExpiryElement options={elementOptions} className="mb-14" />
            </div>
            <div className="col-sm-6">
              <CardCvcElement options={elementOptions} className="mb-14" />
            </div>
          </div>

          {error && <div className="text-danger ah-text-base mb-14">{error}</div>}
        </div>

        <div className="checkout-details">
          <div className="checkout-details-inner px-15 px-sm-20 px-md-34 py-15 py-sm-20 px-md-28">
            <h2 className="ah-heading-04 fw-semibold mb-10 mb-sm-20">Summary</h2>
            <div className="border-primary rounded-4xl p-10 p-sm-20 p-md-30 p-lg-40">
              <div className="bg-theme d-inline-block text-white py-8 px-25 rounded-5 mb-12 ah-text-base fw-semibold mb-12 text-uppercase">
                {plan?.plan_type}
              </div>
              <p className="ah-text-base fw-semibold mb-15 text-black-70">{plan?.description}</p>
              <span className="fw-semibold theme-color ah-heading-01">
                ${plan?.price}
                <span className="ah-text-base fw-normal">/{plan?.plan_type}</span>
              </span>
            </div>
            <hr className="my-15 my-sm-20 my-md-30 my-lg-40" />
            <ul className="list-unstyled mb-21">
              <li className="d-flex justify-content-between mb-15">
                <span className="ah-text-lg fw-semibold text-black">Plan Name</span>
                <span className="fw-semibold text-black-50">{plan?.name}</span>
              </li>
              <li className="d-flex justify-content-between mb-15">
                <span className="ah-text-lg fw-semibold text-black">Price</span>
                <span className="fw-semibold text-black-50">
                  ${plan?.price}/{plan?.plan_type}
                </span>
              </li>
              <li className="d-flex justify-content-between mb-15">
                <span className="ah-text-lg fw-semibold text-black">Tax</span>
                <span className="fw-semibold text-black-50">${plan?.tax}</span>
              </li>
              <li className="d-flex justify-content-between mb-15">
                <span className="ah-text-lg fw-semibold text-black">Total</span>
                <span className="fw-semibold text-black-50">${plan?.totalAmount}</span>
              </li>
            </ul>
            <span className="ah-text-base fw-semibold text-black">
              By purchasing this product you are accepting the <br />
              <span className="theme-color">EGBW Terms & Conditions</span>
            </span>
            <div className="mt-20 mt-sm-40">
              <button
                className="btn btn-primary no-shadow rounded-5 w-100"
                type="submit"
                disabled={!stripe || !elements || isProcessing || !clientSecret || !token}
              >
                {isProcessing ? "Processing..." : "Checkout"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

// ========== StripeFormAndDetails ==========
const StripeFormAndDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const plan = location.state?.plan;

  const [clientSecret, setClientSecret] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        if (!token) {
          toast.error("Please log in to continue.", { position: "top-center" });
          setTimeout(() => navigate("/login"), 2000);
          return;
        }
        if (!plan) {
          toast.error("No plan selected. Please select a plan.", { position: "top-center" });
          setTimeout(() => navigate("/choose-plan"), 2000);
          return;
        }

        const subResponse = await dispatch(getUserSubscriptionAPI(token)).unwrap();
        const { subscription_status } = subResponse.data || {};
        if (subscription_status === "deactivated") {
          toast.error("Your account is deactivated. Please contact support.");
          setTimeout(() => navigate("/support"), 2000);
          return;
        }
        if (subscription_status === "expired") {
          toast("Your subscription has expired. Please renew.", { icon: "⚠️" });
        }

        await dispatch(subscribePlanAPI({ token, planId: plan.id })).unwrap();

        const checkoutResponse = await dispatch(
          createCheckoutSessionAPI({
            token,
            planId: plan.id,
            amount: parseFloat(plan.totalAmount).toFixed(2),
          })
        ).unwrap();

        const { clientSecret, transactionId } = checkoutResponse.data;
        setClientSecret(clientSecret);
        setTransactionId(transactionId);
        localStorage.setItem(`transaction_${plan.id}`, transactionId);
      } catch (error) {
        toast.error(error.message || "Failed to initialize payment.");
      } finally {
        setIsLoading(false);
      }
    };

    if (plan && token) {
      initializePayment();
    } else {
      setIsLoading(false);
    }
  }, [plan, token, navigate, dispatch]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return <div className="text-danger text-center p-6">Payment system not configured.</div>;
  }
  if (isLoading ) {
    return (
      <div className="loader-container">
        <div className="dottedLoading theme-color"></div>
      </div>
    );
  }
  if (!plan) {
    return <div className="text-danger text-center p-6">No plan selected.</div>;
  }


  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm plan={plan} clientSecret={clientSecret} transactionId={transactionId} />
    </Elements>
  );
};

export default StripeFormAndDetails;